<template>
  <!-- Nothing at all when there is nothing to show. An empty "Heard" label
       above an empty row reads as a failure to hear rather than as silence. -->
  <div v-if="partial || words.length" class="sa-voice-transcript">
    <!-- Still being recognised. Not editable, and deliberately set apart: a
         partial can still be rewritten under the user, so it must never look
         like the sentence a command was built from. Same treatment as the
         line this replaces. -->
    <p v-if="partial" class="sa-voice-heard sa-voice-heard-live">
      <span class="sa-voice-heard-label">Hearing</span>
      <span class="sa-voice-heard-text">&ldquo;{{ partial }}&rdquo;</span>
    </p>

    <!-- The final transcript, one button per word. Quotation marks are dropped
         here on purpose: a row of tappable chips inside quotes reads as a
         quotation of the buttons rather than of the speech. -->
    <template v-else>
      <span class="sa-voice-heard-label">Heard</span>
      <div class="sa-voice-words">
        <template v-for="word in words" :key="word.i">
          <input
            v-if="editIndex === word.i"
            ref="editInput"
            v-model="draft"
            type="text"
            class="sa-voice-word-input"
            :style="{ width: inputWidth }"
            :aria-label="'Correct the word ' + word.word"
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            @keydown.enter.stop.prevent="commitEdit"
            @keydown.esc.stop.prevent="cancelEdit"
            @blur="commitEdit"
          />
          <button
            v-else
            type="button"
            class="sa-voice-word"
            :class="{
              'sa-voice-word-flat': !editable,
              'sa-voice-word-inert': editable && editing,
            }"
            :disabled="!editable || editing"
            :aria-label="editable ? 'Edit the word ' + word.word : null"
            @click="startEdit(word.i)"
          >
            {{ word.word }}
          </button>
        </template>
      </div>

      <p v-if="editable" class="sa-voice-word-hint">Tap a word to fix it.</p>
    </template>
  </div>
</template>

<script>
import { replaceWord, tokenize } from "../../utils/voiceTranscriptEdit.js";

/**
 * The heard sentence, with every word tappable.
 *
 * A wrong transcript is the commonest way a voice command goes wrong, and it
 * is nearly always wrong in one word. Re-saying the whole sentence to fix one
 * word is slow and, in a cold room in gloves, frequently produces a second
 * different error. So the sentence itself is the control: tap the word, type
 * the right one, and the corrected sentence goes back out as `reparse` for the
 * dock to re-parse.
 *
 * What this component does NOT do:
 *
 *   - It never edits the live partial. A partial is still moving; an edit
 *     committed against it would be spliced into a string that no longer
 *     exists by the time it lands.
 *   - It never parses anything. The dock owns `parseVoiceTranscript`, because
 *     the dock is what knows about the screen context, the session and what to
 *     do with the proposal that comes back.
 *   - It owns no wrapper chrome. The dock places this inside the conversation
 *     block; a border or margin here would double up with the block's own.
 */
export default {
  name: "VoiceTranscript",
  props: {
    /** The final transcript. Words are rendered from this, and edits splice
        into this exact string, so its offsets are the ones that matter. */
    text: { type: String, default: "" },
    /** The running guess. When present it supersedes `text` — it IS the
        sentence being said now, and the previous one has been answered. */
    partial: { type: String, default: "" },
    /** Off while the session is closed, applying, or otherwise past the point
        where a correction could still change anything. */
    editable: { type: Boolean, default: false },
  },
  emits: ["reparse"],
  data() {
    return {
      /** Index of the word being edited, or -1. Also the guard that makes a
          stray blur after a commit or a cancel a no-op. */
      editIndex: -1,
      draft: "",
    };
  },
  computed: {
    words() {
      return tokenize(this.text);
    },
    editing() {
      return this.editIndex !== -1;
    },
    // Grows with what is typed, so a long correction is not typed into a
    // three-character slot. Border-box, so the padding and the two borders
    // have to be added back on top of the character width.
    inputWidth() {
      const chars = Math.max(String(this.draft).length, 2) + 1;
      return "calc(" + chars + "ch + 1.25rem)";
    },
  },
  watch: {
    // Either of these means the sentence under the open edit has moved. The
    // indices the edit was opened against no longer point at the same words,
    // so the edit is abandoned rather than committed somewhere new. Watchers
    // flush before the re-render, so the input is gone by the time the DOM
    // changes and its blur — if the browser fires one at all — hits the guard.
    text() {
      this.closeEdit();
    },
    partial(value) {
      if (value) this.closeEdit();
    },
  },
  methods: {
    startEdit(i) {
      if (!this.editable || this.editing) return;
      const token = this.words[i];
      if (!token) return;

      this.editIndex = i;
      this.draft = token.word;
      this.$nextTick(() => {
        // `ref` inside a `v-for` is an array in Vue 3 even when one element
        // renders. Read it both ways rather than depending on which, and take
        // the last entry so a not-yet-collected predecessor cannot win.
        const held = this.$refs.editInput;
        const input = Array.isArray(held) ? held[held.length - 1] : held;
        if (!input) return;
        input.focus();
        // Selected, not just focused: the whole point is to replace the word,
        // and typing should do that without a select-all first.
        input.select();
      });
    },

    /**
     * Enter or blur. Emits the whole corrected sentence, never a word — the
     * parser takes sentences, and the dock should not have to rebuild one.
     */
    commitEdit() {
      if (!this.editing) return;
      const i = this.editIndex;
      const token = this.words[i];
      const next = String(this.draft).trim();
      this.closeEdit();

      if (!token) return;
      // An unchanged word is not a correction. Emitting here would cost a
      // parse round trip and re-propose the same command the user was already
      // looking at.
      if (next === token.word) return;
      this.$emit("reparse", replaceWord(this.text, i, next));
    },

    cancelEdit() {
      this.closeEdit();
    },

    closeEdit() {
      this.editIndex = -1;
      this.draft = "";
    },
  },
};
</script>

<style scoped>
/* No Tailwind. public/assets/styles.css is a committed, pre-compiled artefact
   the Vue build does not regenerate, so a utility class not already in it
   silently does nothing. Same reasoning as VoiceCommandModal.vue. */

.sa-voice-heard {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: #334155;
}
.sa-voice-heard-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}
.sa-voice-heard-text {
  display: block;
  overflow-wrap: anywhere;
}
.sa-voice-heard-live .sa-voice-heard-text {
  font-style: italic;
  color: #64748b;
}

/* 6 px gaps, and every word at least 36 px tall. This is tapped in gloves,
   often one-handed, over a dewar — a word that is only as tall as its type is
   a word that gets missed and a neighbour that gets opened instead. */
.sa-voice-words {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0.25rem;
}

.sa-voice-word {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0.25rem 0.5rem;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  background: #f0f9ff;
  color: #0c4a6e;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.sa-voice-word:hover {
  background: #e0f2fe;
}
.sa-voice-word:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 2px;
}

/* Editing is off. The row is the sentence, so it should look like one: no
   chips, no affordance, nothing offering a tap that does nothing. */
.sa-voice-word-flat {
  border-color: transparent;
  background: transparent;
  color: #334155;
  font-weight: 400;
  cursor: default;
}

/* Another word is open. The chips keep their size — a padding change here
   would shift the whole row out from under the input mid-edit — and only dim,
   which is the thing a disabled button has to say. */
.sa-voice-word-inert {
  opacity: 0.45;
  cursor: default;
}

.sa-voice-word-input {
  box-sizing: border-box;
  min-width: 3ch;
  max-width: 100%;
  min-height: 36px;
  padding: 0.25rem 0.5rem;
  border: 2px solid #0c4a6e;
  border-radius: 6px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
}
.sa-voice-word-input:focus {
  outline: 3px solid #0c4a6e;
  outline-offset: 2px;
}

.sa-voice-word-hint {
  margin: 0.375rem 0 0;
  font-size: 13px;
  color: #475569;
}

@media (prefers-reduced-motion: reduce) {
  .sa-voice-word {
    transition: none;
  }
}
</style>
